import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import PageFrame from "./PageFrame";
import api from "../bootstrapApi";
import DirectoryRowCard from "../components/DirectoryRowCard";
import {
  saasCircleCountSx,
  saasDirectoryWrapSx,
  saasHeroActionButtonSx,
  saasInputSx,
  saasSectionCardSx,
  saasSoftCountSx,
} from "../components/saasStyles";

const initialInterestForm = {
  categoryId: "",
  productId: "",
  displayName: "",
  whatsappNumber: "",
  locationText: "",
  notes: "",
};

export default function WhatsAppPage() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [form, setForm] = useState(initialInterestForm);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState("");
  const [quickAction, setQuickAction] = useState("default");
  const [flowOpen, setFlowOpen] = useState(false);

  const loadMessages = async () => {
    const response = await api.get("/api/whatsapp/messages");
    setMessages(response.data);
  };

  useEffect(() => {
    api
      .get("/api/whatsapp-entry/categories")
      .then((response) => setCategories(response.data))
      .catch(() => setMessage({ type: "error", text: "Les categories WhatsApp n'ont pas pu etre chargees." }));

    loadMessages().catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.categoryId) {
      setProducts([]);
      setSelectedProductId(null);
      return;
    }

    api
      .get(`/api/whatsapp-entry/categories/${form.categoryId}/products`)
      .then((response) => {
        setProducts(response.data);
      })
      .catch(() => setMessage({ type: "error", text: "Les produits de cette categorie n'ont pas pu etre charges." }));
  }, [form.categoryId]);

  const filteredProducts = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const base = !needle
      ? products
      : products.filter((product) =>
          [product.name, product.description, product.category_name]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(needle)
        );

    if (quickAction === "standard") {
      return base.filter((product) => product.product_type === "standard");
    }
    if (quickAction === "category" && form.categoryId) {
      return base.filter((product) => String(product.category_id) === String(form.categoryId));
    }
    return base;
  }, [products, search, quickAction, form.categoryId]);

  const handleInterestSubmit = async () => {
    try {
      const response = await api.post("/api/whatsapp-entry/interest", {
        ...form,
        productId: Number(form.productId),
      });
      setMessage({
        type: "success",
        text: `Le prospect ${response.data.lead.id} a ete cree via l'entree WhatsApp.`,
      });
      setForm(initialInterestForm);
      setProducts([]);
      setSelectedProductId(null);
      setFlowOpen(false);
      await loadMessages();
    } catch (_error) {
      setMessage({ type: "error", text: "L'interet n'a pas pu etre enregistre." });
    }
  };

  return (
    <PageFrame
      eyebrow="SmartDoctors"
      title="Centre WhatsApp"
      description="Vue du point d'entree WhatsApp, de la file d'envoi et des messages clients prepares."
      stats={[]}
      action={
        <Button variant="contained" startIcon={<AddCircleRoundedIcon />} onClick={() => setFlowOpen(true)} sx={saasHeroActionButtonSx}>
          + Nouveau flux
        </Button>
      }
    >
      {message ? <Alert severity={message.type}>{message.text}</Alert> : null}

      <Box sx={{ ...saasDirectoryWrapSx, borderRadius: "44px", px: { xs: 2, md: 2.5 }, py: { xs: 1.2, md: 1.4 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
          Afficher {filteredProducts.length} elements
        </Typography>

        <Stack direction={{ xs: "column", xl: "row" }} spacing={1.25} alignItems={{ xs: "stretch", xl: "center" }}>
          <TextField
            label="Rechercher"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{
              ...saasInputSx,
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "18px",
                minHeight: 44,
                backgroundColor: "#fff",
                boxShadow: "none",
              },
            }}
            InputProps={{ startAdornment: <SearchRoundedIcon sx={{ color: "#5f6fff", mr: 1 }} /> }}
          />

          <FormControl sx={{ minWidth: { xs: "100%", xl: 350 } }}>
            <Select
              value={quickAction}
              onChange={(event) => setQuickAction(event.target.value)}
              displayEmpty
              sx={{ minHeight: 56, borderRadius: "18px", bgcolor: "#fff", fontWeight: 700, boxShadow: "none" }}
            >
              <MenuItem value="default">Action rapide</MenuItem>
              <MenuItem value="standard">Produits standards</MenuItem>
              <MenuItem value="category">Categorie active</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ ...saasSoftCountSx, minWidth: 106, minHeight: 44, bgcolor: "#f5f7ff" }}>
            <Typography variant="body2" sx={{ color: "#5867ff", fontWeight: 700 }}>
              {messages.filter((item) => item.status === "queued").length} en attente
            </Typography>
          </Box>

          <Box sx={{ width: 58, height: 44, borderRadius: "16px", border: "1px solid #dfe6f4", bgcolor: "#fff", display: "grid", placeItems: "center" }}>
            <Badge badgeContent={messages.length} color="error">
              <IconButton size="small" sx={{ color: "#5a68ff" }}>
                <NotificationsRoundedIcon />
              </IconButton>
            </Badge>
          </Box>
        </Stack>

        <Stack direction={{ xs: "column", xl: "row" }} spacing={1.25} sx={{ mt: 1.25 }}>
          <TextField
            select
            label="Categorie"
            value={form.categoryId}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                categoryId: event.target.value,
                productId: "",
              }))
            }
            sx={{ ...saasInputSx, flex: 1 }}
          >
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Produit"
            value={form.productId}
            onChange={(event) => {
              setForm((current) => ({ ...current, productId: event.target.value }));
              setSelectedProductId(Number(event.target.value));
            }}
            sx={{ ...saasInputSx, flex: 1 }}
            disabled={!form.categoryId}
          >
            {products.map((product) => (
              <MenuItem key={product.id} value={product.id}>
                {product.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Box>

      <Box sx={{ ...saasSectionCardSx, borderRadius: "44px", display: "grid", gap: 2.25, px: { xs: 2, md: 2.4 }, py: { xs: 1.4, md: 1.8 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>
              Catalogue WhatsApp
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Vue structuree avec categorie, apercu produit et points d'entree de conversion.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.25}>
            <Box sx={saasCircleCountSx}>
              <Box>
                <Typography variant="caption" sx={{ color: "#7d89a2" }}>
                  Visibles
                </Typography>
                <Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>
                  {filteredProducts.length}
                </Typography>
              </Box>
            </Box>
            <Box sx={saasCircleCountSx}>
              <Box>
                <Typography variant="caption" sx={{ color: "#7d89a2" }}>
                  File d'envoi
                </Typography>
                <Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>
                  {messages.filter((item) => item.status === "queued").length}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>

        {filteredProducts.map((product) => (
          <DirectoryRowCard
            key={product.id}
            selected={selectedProductId === product.id}
            avatarText={(product.name || "P").slice(0, 1).toUpperCase()}
            title={product.name}
            subtitle={`WA-${product.id}`}
            chips={[
              { label: product.category_name || "Categorie", color: "success" },
              { label: product.product_type || "standard", color: "default" },
            ]}
            columns={[
              {
                label: "Description",
                value: product.description || "-",
                secondary: `${Number(product.price || 0).toFixed(2)} ${product.currency || "EUR"}`,
              },
              {
                label: "Flux d'entree",
                value: form.categoryId ? "Categorie active" : "Choisir une categorie",
                secondary: selectedProductId === product.id ? "Pret pour la conversion" : "Produit selectionnable",
              },
            ]}
              actionLabel={selectedProductId === product.id ? "Selectionne" : "Choisir"}
            onSelect={() => {
              setSelectedProductId(product.id);
              setForm((current) => ({
                ...current,
                categoryId: String(product.category_id),
                productId: String(product.id),
              }));
            }}
          />
        ))}
      </Box>

      <Box sx={{ ...saasSectionCardSx, borderRadius: "44px", display: "grid", gap: 2.25, px: { xs: 2, md: 2.4 }, py: { xs: 1.4, md: 1.8 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: 18, mb: 0.3 }}>
              File d'envoi WhatsApp
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Messages sortants prepares pour les clients et pour l'envoi ulterieur.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.25}>
            <Box sx={saasCircleCountSx}>
              <Box>
                <Typography variant="caption" sx={{ color: "#7d89a2" }}>
                  En attente
                </Typography>
                <Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>
                  {messages.filter((item) => item.status === "queued").length}
                </Typography>
              </Box>
            </Box>
            <Box sx={saasCircleCountSx}>
              <Box>
                <Typography variant="caption" sx={{ color: "#7d89a2" }}>
                  Remplace
                </Typography>
                <Typography variant="h6" sx={{ color: "#25324b", textAlign: "center" }}>
                  {messages.filter((item) => item.status === "superseded").length}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>

        {messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Aucun message prepare pour le moment.
          </Typography>
        ) : (
          messages.map((item) => (
            <DirectoryRowCard
              key={item.id}
              avatarText={(item.contact_name || "W").slice(0, 1).toUpperCase()}
              title={item.contact_name || item.whatsapp_number || "Destinataire inconnu"}
              subtitle={item.template_name || `MSG-${item.id}`}
              chips={[
                { label: item.status, color: item.status === "queued" ? "success" : "default" },
                { label: item.recipient_role || "client", color: "default" },
              ]}
              columns={[
                {
                  label: "Cible",
                  value: item.whatsapp_number || "-",
                  secondary: `${item.entity_type || "-"} #${item.entity_id || "-"}`,
                },
                {
                  label: "Contenu",
                  value: item.content || "-",
                  secondary: new Date(item.created_at).toLocaleString("fr-FR"),
                },
              ]}
              actionLabel="Prepare"
              onSelect={() => {}}
            />
          ))
        )}
      </Box>

      <Dialog open={flowOpen} onClose={() => setFlowOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Enregistrer un interet WhatsApp</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 1.5 }}>
          <TextField
            select
            label="Categorie"
            value={form.categoryId}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                categoryId: event.target.value,
                productId: "",
              }))
            }
            sx={saasInputSx}
          >
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Produit standard"
            value={form.productId}
            onChange={(event) => setForm((current) => ({ ...current, productId: event.target.value }))}
            sx={saasInputSx}
            disabled={!form.categoryId}
          >
            {products.map((product) => (
              <MenuItem key={product.id} value={product.id}>
                {product.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Nom" value={form.displayName} onChange={(event) => setForm((current) => ({ ...current, displayName: event.target.value }))} sx={saasInputSx} />
          <TextField label="Numero WhatsApp" value={form.whatsappNumber} onChange={(event) => setForm((current) => ({ ...current, whatsappNumber: event.target.value }))} sx={saasInputSx} />
          <TextField label="Localisation (optionnel)" value={form.locationText} onChange={(event) => setForm((current) => ({ ...current, locationText: event.target.value }))} sx={saasInputSx} />
          <TextField label="Note" multiline minRows={3} value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} sx={saasInputSx} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlowOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleInterestSubmit} disabled={!form.productId}>
            Je suis interesse
          </Button>
        </DialogActions>
      </Dialog>
    </PageFrame>
  );
}
